<template>
    <button
        type="button"
        class="app-launcher__trigger app-launcher__trigger--icon"
        aria-label="Search"
        @click="openPalette"
    >
        <span class="fas fa-search"></span>
    </button>
    <button
        type="button"
        class="app-launcher__trigger app-launcher__trigger--hint"
        aria-label="Search"
        @click="openPalette"
    >
        <span class="fas fa-search app-launcher__trigger-icon"></span>
        <span class="app-launcher__trigger-hint">{{ keyboardShortcut }}</span>
    </button>
    <teleport to="body">
        <div
            v-if="isOpen"
            class="app-launcher"
            @click.self="closePalette"
            @keydown.esc="closePalette"
        >
            <dialog
                class="app-launcher__dialog"
                open
                aria-modal="true"
                aria-labelledby="app-launcher-search-label"
                @keydown="onDialogKeydown"
            >
                <div class="app-launcher__search">
                    <span class="app-launcher__search-icon fas fa-search" aria-hidden="true"></span>
                    <label id="app-launcher-search-label" class="app-launcher__sr-only" for="app-launcher-search">
                        Search
                    </label>
                    <input
                        id="app-launcher-search"
                        ref="queryInput"
                        v-model="query"
                        class="app-launcher__input"
                        type="text"
                        placeholder="Search..."
                        autocomplete="off"
                    />
                    <kbd class="app-launcher__esc">Esc</kbd>
                </div>
                <div class="app-launcher__results">
                    <template v-if="showResults">
                        <template v-for="section in sections" :key="section.title">
                            <div class="app-launcher__section-label">{{ section.title }}</div>
                            <div v-if="section.searching" class="app-launcher__hint">Searching records...</div>
                            <button
                                v-for="item in section.items"
                                :key="item.id"
                                type="button"
                                class="app-launcher__item"
                                :class="{ 'app-launcher__item--active': item.id === activeId }"
                                @mouseenter="activeId = item.id"
                                @click="openItem(item)"
                            >
                                <span v-if="item.badge" class="app-launcher__item-badge">{{ item.badge }}</span>
                                <span class="app-launcher__item-copy">
                                    <span class="app-launcher__item-title">{{ item.title }}</span>
                                    <span v-if="item.subtitle" class="app-launcher__item-subtitle">{{ item.subtitle }}</span>
                                </span>
                            </button>
                        </template>
                        <div v-if="!flatItems.length && !searchingRecords" class="app-launcher__empty">No matches.</div>
                    </template>
                    <div v-else class="app-launcher__empty">Type to search...</div>
                </div>
            </dialog>
        </div>
    </teleport>
</template>

<script>
import debounce from 'lodash/debounce';
import { mapActions } from 'vuex';
import { isChartsView } from '@/data/charts';
import { buildChartBrowseSections, buildViewSections } from '@/data/view-search';

const RECORD_BADGES = {
    transaction: 'Transaction',
    activity: 'Activity',
    account: 'Account',
    admin: 'Admin',
    partner: 'Partner',
    customer: 'Customer',
    customer_activity: 'Customer',
    customer_transactions: 'Customer',
    order_activity: 'Order',
    order_transactions: 'Order',
};

const RECORD_SCOPES = {
    transaction: ['read_transactions', 'write_transactions'],
    customer_transactions: ['read_transactions', 'write_transactions'],
    order_transactions: ['read_transactions', 'write_transactions'],
    activity: ['read_activity', 'write_activity'],
    customer_activity: ['read_activity', 'write_activity'],
    order_activity: ['read_activity', 'write_activity'],
    account: ['read_accounts', 'write_accounts'],
    partner: ['read_accounts', 'write_accounts'],
    customer: ['read_accounts', 'write_accounts'],
    admin: ['read_admins', 'write_admins'],
};

export default {
    name: 'AppLauncher',
    data() {
        return {
            isOpen: false,
            query: '',
            activeId: '',
            recordResults: [],
            searchingRecords: false,
            abortController: null,
        };
    },
    computed: {
        hasQuery() {
            return Boolean(this.query.trim());
        },
        inCharts() {
            return isChartsView(this.$route.path);
        },
        keyboardShortcut() {
            const isMac = /mac|ipod|iphone|ipad/i.test(navigator.platform);
            return isMac ? '⌘K' : 'Ctrl+K';
        },
        showResults() {
            return this.hasQuery || this.inCharts;
        },
        catalog() {
            return buildViewSections();
        },
        browseSections() {
            return buildChartBrowseSections()
                .map((section) => ({
                    ...section,
                    items: section.items.filter((item) => this.canOpen(item)),
                }))
                .filter((section) => section.items.length > 0);
        },
        permittedSections() {
            return this.catalog
                .map((section) => ({
                    ...section,
                    items: section.items.filter((item) => this.canOpen(item)),
                }))
                .filter((section) => section.items.length > 0);
        },
        permittedItems() {
            return this.permittedSections.flatMap((section) =>
                section.items.map((item) => ({ ...item, groupTitle: section.title })),
            );
        },
        matchedItems() {
            if (!this.hasQuery) {
                return [];
            }
            const query = this.query.trim().toLowerCase();
            return this.permittedItems.filter((item) => {
                const haystack = `${item.groupTitle} ${item.title}`.toLowerCase();
                return haystack.includes(query);
            });
        },
        recordItems() {
            return this.recordResults
                .map((result, index) => ({
                    id: `record-${result.kind}-${index}`,
                    title: result.title,
                    subtitle: result.subtitle,
                    badge: RECORD_BADGES[result.kind] || 'Record',
                    to: result.to,
                    account: result.account,
                    scopes: RECORD_SCOPES[result.kind],
                }))
                .filter((item) => this.canOpen(item));
        },
        recordSection() {
            if (!this.searchingRecords && !this.recordItems.length) {
                return null;
            }
            return {
                title: 'Records',
                searching: this.searchingRecords && !this.recordItems.length,
                items: this.recordItems,
            };
        },
        sections() {
            const view_sections = this.viewSections;
            if (!this.recordSection) {
                return view_sections;
            }
            return [this.recordSection, ...view_sections];
        },
        viewSections() {
            if (!this.hasQuery && this.inCharts) {
                return this.browseSections;
            }
            return this.permittedSections
                .map((section) => ({
                    title: section.title,
                    items: this.matchedItems.filter((item) => item.groupTitle === section.title),
                }))
                .filter((section) => section.items.length > 0);
        },
        flatItems() {
            return this.sections.flatMap((section) => section.items);
        },
    },
    watch: {
        isOpen(open) {
            if (open) {
                this.query = '';
                this.activeId = '';
                this.recordResults = [];
                this.searchingRecords = false;
                this.$nextTick(() => this.$refs.queryInput?.focus());
                return;
            }
            this.debouncedFetchRecords.cancel();
            this.abortController?.abort();
        },
        query() {
            if (!this.isOpen) {
                return;
            }
            this.debouncedFetchRecords();
        },
        flatItems(items) {
            if (!items.some((item) => item.id === this.activeId)) {
                this.activeId = items[0]?.id || '';
            }
        },
    },
    created() {
        this.debouncedFetchRecords = debounce(function fetchRecords() {
            this.fetchRecords();
        }, 300);
    },
    mounted() {
        window.addEventListener('keydown', this.onWindowKeydown);
    },
    beforeUnmount() {
        this.debouncedFetchRecords.cancel();
        this.abortController?.abort();
        window.removeEventListener('keydown', this.onWindowKeydown);
    },
    methods: {
        ...mapActions({ setAccount: 'globalFilters/setAccount' }),
        canOpen(item) {
            if (!item.scopes) {
                return true;
            }
            return this.CAN(item.scopes, undefined, item.bypassPositions);
        },
        openPalette() {
            this.isOpen = true;
        },
        closePalette() {
            this.isOpen = false;
        },
        openItem(item) {
            this.closePalette();
            if (item.account?.id) {
                this.setAccount(item.account);
            }
            if (item.to) {
                this.$router.push(item.to).catch(() => {});
            }
        },
        async fetchRecords() {
            const term = this.query.trim();
            if (!this.isOpen || term.length < 2) {
                this.recordResults = [];
                this.searchingRecords = false;
                return;
            }
            this.abortController?.abort();
            const controller = new AbortController();
            this.abortController = controller;
            this.recordResults = [];
            this.searchingRecords = true;
            try {
                const { data } = await this.$axios.get('/_/search', {
                    params: { q: term },
                    signal: controller.signal,
                });
                if (controller !== this.abortController) {
                    return;
                }
                this.recordResults = Array.isArray(data?.results) ? data.results : [];
                this.searchingRecords = false;
            } catch (error) {
                if (error?.code === 'ERR_CANCELED' || controller !== this.abortController) {
                    return;
                }
                this.recordResults = [];
                this.searchingRecords = false;
            }
        },
        moveActive(delta) {
            const items = this.flatItems;
            if (!items.length) {
                return;
            }
            const index = items.findIndex((item) => item.id === this.activeId);
            const next = (index + delta + items.length) % items.length;
            this.activeId = items[next].id;
        },
        onWindowKeydown(event) {
            if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
                event.preventDefault();
                if (this.isOpen) {
                    this.closePalette();
                } else {
                    this.openPalette();
                }
            }
        },
        onDialogKeydown(event) {
            if (event.key === 'Escape') {
                event.preventDefault();
                this.closePalette();
                return;
            }
            if (event.key === 'ArrowDown') {
                event.preventDefault();
                this.moveActive(1);
                return;
            }
            if (event.key === 'ArrowUp') {
                event.preventDefault();
                this.moveActive(-1);
                return;
            }
            if (event.key === 'Enter') {
                event.preventDefault();
                const item = this.flatItems.find((row) => row.id === this.activeId);
                if (item) {
                    this.openItem(item);
                }
            }
        },
    },
};
</script>

<style lang="scss">
.app-launcher__trigger {
    align-items: center;
    background: transparent;
    color: $ecart-secondary-500;
    cursor: pointer;
    display: none;
    justify-content: center;
    transition: background-color 0.15s ease, border-color 0.15s ease, color 0.15s ease;

    &--icon {
        border: none;
        border-radius: 8px;
        color: #495057;
        display: inline-flex;
        flex-shrink: 0;
        font-size: 16px;
        height: 36px;
        padding: 0;
        width: 36px;

        @media (min-width: map-get($grid-breakpoints, md)) {
            display: none;
        }

        &:hover {
            background: $ecart-secondary-100;
            color: $general-black;
        }
    }

    &--hint {
        border: 1px solid $ecart-secondary-200;
        border-radius: 8px;
        gap: 8px;
        height: 34px;
        margin-right: 8px;
        padding: 0 12px;

        @media (min-width: map-get($grid-breakpoints, md)) {
            display: inline-flex;
        }

        &:hover {
            border-color: $ecart-secondary-400;
            color: $general-black;
        }
    }

    &-icon {
        font-size: 14px;
    }

    &-hint {
        background: $white;
        border: 1px solid $ecart-secondary-200;
        border-radius: 6px;
        color: $ecart-secondary-500;
        font-size: 11px;
        font-weight: 600;
        line-height: 1;
        padding: 3px 5px;
    }
}

.app-launcher {
    align-items: flex-start;
    background: rgba(15, 18, 24, 0.45);
    display: flex;
    inset: 0;
    justify-content: center;
    padding: 18vh 16px 16px;
    position: fixed;
    z-index: 2000;
}

.app-launcher__dialog {
    background: $white;
    border: none;
    border-radius: 12px;
    box-shadow: 0 16px 40px rgba(0, 0, 0, 0.18);
    display: flex;
    flex-direction: column;
    margin: 0;
    max-height: 520px;
    max-width: none;
    padding: 0;
    position: relative;
    width: min(560px, 100%);
}

.app-launcher__search {
    align-items: center;
    border-bottom: 1px solid $ecart-secondary-200;
    display: flex;
    gap: 10px;
    padding: 14px 16px;
}

.app-launcher__search-icon {
    color: $ecart-secondary-400;
}

.app-launcher__input {
    border: 0;
    flex: 1;
    font-size: 15px;
    outline: none;
}

.app-launcher__esc {
    background: $white;
    border: 1px solid $ecart-secondary-200;
    border-radius: 6px;
    color: $ecart-secondary-500;
    font-size: 11px;
    font-weight: 600;
    padding: 2px 7px;
}

.app-launcher__sr-only {
    border: 0;
    clip: rect(0, 0, 0, 0);
    height: 1px;
    margin: -1px;
    overflow: hidden;
    padding: 0;
    position: absolute;
    white-space: nowrap;
    width: 1px;
}

.app-launcher__results {
    align-items: stretch;
    display: flex;
    flex-direction: column;
    flex: 1;
    min-height: 180px;
    overflow-y: auto;
    padding: 8px 0;
}

.app-launcher__section-label {
    color: $ecart-secondary-400;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.04em;
    padding: 8px 16px 4px;
    text-transform: uppercase;
}

.app-launcher__item {
    align-items: center;
    background: transparent;
    border: 0;
    color: $general-black;
    display: flex;
    gap: 10px;
    padding: 8px 16px;
    text-align: left;
    width: 100%;

    &--active {
        background: $ecart-primary-100;
    }
}

.app-launcher__item-badge {
    background: $ecart-secondary-100;
    border-radius: 4px;
    color: $ecart-secondary-500;
    flex-shrink: 0;
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.03em;
    padding: 2px 6px;
    text-transform: uppercase;
}

.app-launcher__item-copy {
    display: flex;
    flex: 1;
    flex-direction: column;
    min-width: 0;
}

.app-launcher__item-title {
    font-size: 14px;
}

.app-launcher__item-subtitle {
    color: $ecart-secondary-500;
    font-size: 12px;
}

.app-launcher__hint {
    color: $ecart-secondary-400;
    font-size: 13px;
    padding: 8px 16px;
}

.app-launcher__empty {
    align-items: center;
    color: $ecart-secondary-400;
    display: flex;
    flex: 1;
    font-size: 14px;
    justify-content: center;
    min-height: 160px;
    padding: 16px;
}
</style>
